import React, { useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Check, Copy, ExternalLink, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { TemplateCreator } from "./TemplateCreator";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import type { WaSettings, WaTemplate } from "@/types/whatsapp";
import { useConfirm } from "@/context/ConfirmContext";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmt = (v?: string | null) =>
  v ? new Date(v).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })) : "Never";

/** Admin: connect the company number (Meta Cloud API) and manage templates */
export function WhatsAppSettings({ onSaved, section = "connection" }: { onSaved: () => void; section?: "connection" | "templates" }) {
  const confirm = useConfirm();
  const { success, error } = useToast();
  const [settings, setSettings] = useState<WaSettings | null>(null);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [form, setForm] = useState({
    phone_number_id: "",
    waba_id: "",
    access_token: "",
    app_secret: "",
    default_country_code: "91",
    auto_create_lead: false,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const removeTemplate = async (name: string) => {
    const ok = await confirm({
      title: `Delete template "${name}"?`,
      message: "It is removed from WhatsApp in all its languages. Automations using it stop sending.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/WhatsApp/Templates/${encodeURIComponent(name)}`);
      success("Template deleted");
      load();
    } catch (err: any) {
      error("Could not delete", err.message);
    }
  };

  const load = () => {
    api
      .get("/api/WhatsApp/Settings")
      .then((res) => {
        const s = unwrapObject<WaSettings>(res);
        setSettings(s);
        if (s.account) {
          setForm((f) => ({
            ...f,
            phone_number_id: s.account!.phone_number_id,
            waba_id: s.account!.waba_id,
            default_country_code: s.account!.default_country_code,
            auto_create_lead: s.account!.auto_create_lead,
            access_token: "",
            app_secret: "",
          }));
        }
      })
      .catch((err) => error("Could not load settings", err.message));
    api
      .get("/api/WhatsApp/Templates?all=true")
      .then((res) => setTemplates(unwrapArray<WaTemplate>(res)))
      .catch(() => setTemplates([]));
  };

  useEffect(load, []);

  const copy = async (key: string, value: string) => {
    await navigator.clipboard.writeText(value).catch(() => undefined);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/WhatsApp/Settings", {
        phone_number_id: form.phone_number_id.trim(),
        waba_id: form.waba_id.trim(),
        access_token: form.access_token.trim() || undefined,
        app_secret: form.app_secret.trim() || undefined,
        default_country_code: form.default_country_code.trim() || "91",
        auto_create_lead: form.auto_create_lead,
      });
      success("WhatsApp connected", "Settings checked and saved");
      load();
      onSaved();
    } catch (err: any) {
      error("Could not save", err.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = unwrapObject<any>(await api.post("/api/WhatsApp/TestConnection"));
      if (res.ok) success("Connection OK", `${res.verified_name} · ${res.display_phone_number}`);
      else error("Connection failed", res.error);
      load();
    } finally {
      setTesting(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = unwrapObject<any>(await api.post("/api/WhatsApp/SyncTemplates"));
      success("Templates synced", `${res.synced} template(s) from WhatsApp`);
      load();
    } catch (err: any) {
      error("Sync failed", err.message);
    } finally {
      setSyncing(false);
    }
  };

  const acc = settings?.account;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Settings → WhatsApp shows these as separate tabs
  if (section === "templates") {
    return (
      <div className="max-w-6xl">
        <div className="bg-card border rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-5 py-3 border-b">
            <div>
              <h2 className="text-sm font-bold">Message templates</h2>
              <p className="text-[11px] text-muted-foreground">Last synced: {fmt(acc?.templates_synced_at)}</p>
            </div>
            <div className="flex gap-2">
              <a
                href="https://business.facebook.com/wa/manage/message-templates/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Create in WhatsApp <ExternalLink className="size-3" />
              </a>
              <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setCreatorOpen(true)} disabled={!acc}>
                <Plus className="size-3.5" />
                New template
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={sync} disabled={syncing || !acc}>
                {syncing ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
                Sync templates
              </Button>
            </div>
          </div>
          {templates.length === 0 ? (
            <div className="px-5 py-4 text-xs text-muted-foreground">No templates yet.</div>
          ) : (
            <ul className="divide-y max-h-[420px] overflow-y-auto">
              {templates.map((t) => (
                <li key={t.id} className="px-5 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-semibold flex-1 truncate">{t.name}</span>
                    <span className="text-[10px] text-muted-foreground">{t.language}</span>
                    <Badge variant="outline" className="text-[10px]">{t.category}</Badge>
                    <Badge
                      variant={t.template_status === "APPROVED" ? "success" : t.template_status === "PENDING" ? "warning" : "destructive"}
                      className="text-[10px]"
                    >
                      {t.template_status}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => removeTemplate(t.name)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Delete template ${t.name}`}
                      title="Delete template"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.body_text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
        <TemplateCreator open={creatorOpen} onOpenChange={setCreatorOpen} onCreated={load} />
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2 max-w-6xl">
      <div className="space-y-4">
        {acc && (
          <div className="bg-card border rounded-2xl p-5 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <WhatsAppIcon className="size-8" />
                <div>
                  <div className="font-semibold">{acc.verified_name || "WhatsApp number"}</div>
                  <div className="text-xs text-muted-foreground">{acc.display_phone_number}</div>
                </div>
              </div>
              <Badge variant={acc.connection_status === "CONNECTED" ? "success" : "destructive"}>
                {acc.connection_status === "CONNECTED" ? "Connected" : "Error"}
              </Badge>
            </div>
            {acc.last_error && <p className="text-xs text-destructive">{acc.last_error}</p>}
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-1">
              <span>Quality rating: <strong className="text-foreground">{acc.quality_rating || "—"}</strong></span>
              <span>Last message received: <strong className="text-foreground">{fmt(acc.last_webhook_at)}</strong></span>
            </div>
            <Button size="sm" variant="outline" onClick={test} disabled={testing} className="gap-1.5">
              {testing ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
              Test connection
            </Button>
          </div>
        )}

        <form onSubmit={save} autoComplete="off" className="bg-card border rounded-2xl p-5 space-y-3">
          <div>
            <h2 className="text-sm font-bold">{acc ? "Update connection" : "Connect the company WhatsApp number"}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Copy these from your WhatsApp business account's setup page. Use a key that never expires.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">WhatsApp number ID *</Label>
              <Input
                required
                name="wa-phone-number-id"
                autoComplete="off"
                inputMode="numeric"
                value={form.phone_number_id}
                onChange={set("phone_number_id")}
                placeholder="1234567890123456"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">WhatsApp business account ID *</Label>
              <Input
                required
                name="wa-business-account-id"
                autoComplete="off"
                inputMode="numeric"
                value={form.waba_id}
                onChange={set("waba_id")}
                placeholder="1234567890123456"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Message service key {acc?.has_access_token ? "(leave empty to keep)" : "*"}</Label>
              <Input
                type="password"
                name="wa-access-token"
                autoComplete="new-password"
                required={!acc}
                value={form.access_token}
                onChange={set("access_token")}
                placeholder="EAAG…"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Secret code {acc?.has_app_secret ? "(leave empty to keep)" : "*"}</Label>
              <Input
                type="password"
                name="wa-app-secret"
                autoComplete="new-password"
                required={!acc}
                value={form.app_secret}
                onChange={set("app_secret")}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Default country code</Label>
              <Input value={form.default_country_code} onChange={set("default_country_code")} placeholder="91" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
            <div>
              <div className="text-xs font-medium">Create a lead for unknown numbers</div>
              <div className="text-[11px] text-muted-foreground">When someone not in the CRM messages you (source: WhatsApp)</div>
            </div>
            <Switch checked={form.auto_create_lead} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_create_lead: v }))} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin mr-1.5" />}
              {acc ? "Check & save" : "Check & connect"}
            </Button>
          </div>
        </form>
      </div>

      <div className="space-y-4">
        <div className="bg-card border rounded-2xl p-5 space-y-3">
          <h2 className="text-sm font-bold">Incoming message link</h2>
          <p className="text-xs text-muted-foreground">
            Paste these into your WhatsApp business account's settings so customer messages reach the CRM. Then switch on <strong>messages</strong>. The link must open from the internet with a secure (https) address.
          </p>
          <CopyRow label="Message link" value={settings?.webhook_url || ""} copied={copied === "url"} onCopy={() => copy("url", settings?.webhook_url || "")} />
          {settings?.verify_token ? (
            <CopyRow label="Check code" value={settings.verify_token} copied={copied === "token"} onCopy={() => copy("token", settings.verify_token!)} />
          ) : (
            <p className="text-xs text-muted-foreground">The check code appears after you save the connection.</p>
          )}
        </div>
        {acc && <CampaignCap />}
      </div>
    </div>
  );
}

/** How many new conversations a campaign may start per day: Meta's limit for the number (it starts at 250 and grows with good quality) */
function CampaignCap() {
  const { success, error } = useToast();
  const [cap, setCap] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/api/WhatsAppCampaign/Settings").then((r) => setCap(String(unwrapObject<any>(r).daily_cap ?? 250))).catch(() => undefined); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/api/WhatsAppCampaign/Settings", { daily_cap: Number(cap) });
      success("Saved", "New campaigns use this daily limit.");
    } catch (e: any) {
      error("Couldn't save", e?.message || "Enter a whole number of at least 1");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="bg-card border rounded-2xl p-5 space-y-3">
      <h2 className="text-sm font-bold">Campaign daily limit</h2>
      <p className="text-xs text-muted-foreground">
        The most new conversations a WhatsApp campaign may start in one day. WhatsApp sets this for your number (250 to begin with, then 1,000 and more as your
        message quality stays good); see it in your WhatsApp business account. A campaign bigger than this continues the next day.
      </p>
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor="wa-cap" className="text-xs">Messages per day</Label>
          <Input id="wa-cap" inputMode="numeric" className="w-32" value={cap} onChange={(e) => setCap(e.target.value.replace(/\D/g, ""))} />
        </div>
        <Button type="button" size="sm" disabled={!cap || saving} onClick={save}>{saving ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

function CopyRow({ label, value, copied, onCopy }: { label: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value} className="font-mono text-xs" />
        <Button type="button" variant="outline" size="sm" className="h-9 w-9 p-0 shrink-0" onClick={onCopy} aria-label={`Copy ${label}`}>
          {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
        </Button>
      </div>
    </div>
  );
}
